/**
 * Local queries for Agape DOMAIN (F0b + F1) over PowerSync SQLite.
 *
 * Isolation is ALREADY resolved upstream: buckets parameterized by
 * `effective_grant` guarantee local SQLite only contains rows the
 * account can view, so these queries do not re-filter by permissions (UI
 * uses `canAt`/`effective_grant` only to gate ACTIONS, not visibility).
 */

import {
  activity_photo,
  asc,
  attendance_entry,
  attendance_session,
  capability,
  church,
  church_group,
  church_relation,
  cleaning_assignment,
  cleaning_shift,
  desc,
  devotional_followup,
  devotional_item,
  devotional_plan,
  devotional_progress,
  effective_grant,
  eq,
  finance_category,
  finance_record,
  fund,
  group_category,
  group_category_capability,
  group_member,
  group_type,
  inventory_item,
  inventory_movement,
  isNull,
  meeting,
  meeting_activity,
  meeting_exception,
  meeting_invite,
  membership,
  person,
  person_relation,
  type PowerSyncDrizzleDB,
  prayer_request,
  projection_item,
  rehearsal,
  scene_element,
  scene_photo,
  scene_work,
  schedule_block,
  setlist,
  setlist_song,
  song,
  song_part,
  venue,
  venue_image,
} from "@agape/powersync";

/** Synchronized churches (outside trash), ordered to assemble tree. */
export function getChurchesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(church).where(isNull(church.deleted_at)).orderBy(asc(church.path));
}

/** Non-hierarchical relationships between visible churches. */
export function getChurchRelationsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(church_relation).orderBy(asc(church_relation.kind));
}

/** Locales visibles (fuera papelera). */
export function getVenuesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(venue).where(isNull(venue.deleted_at)).orderBy(asc(venue.name));
}

/** Visible venue images (gallery). */
export function getVenueImagesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(venue_image)
    .orderBy(asc(venue_image.sort_order), asc(venue_image.created_at));
}

/** Visible projection items, in projection order. */
export function getProjectionItemsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(projection_item)
    .where(isNull(projection_item.deleted_at))
    .orderBy(asc(projection_item.sort_order), asc(projection_item.title));
}

/** Effective permissions of active account (same governing sync). */
export function getEffectiveGrantsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(effective_grant).where(eq(effective_grant.user_id, userId));
}

// ─── F1: directorio de personas ──────────────────────────────────────────────

/** Directorio de personas visible (fuera papelera). */
export function getPeopleQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(person).where(isNull(person.deleted_at)).orderBy(asc(person.name));
}

/** Relaciones de familia visibles. */
export function getPersonRelationsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(person_relation).orderBy(asc(person_relation.kind));
}

/** Visible person<->church memberships (outside trash). */
export function getMembershipsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(membership)
    .where(isNull(membership.deleted_at))
    .orderBy(asc(membership.created_at));
}

// ─── F1: groups (category × proper name × instance) ─────────────────────────

/** Global capabilities catalog. */
export function getCapabilitiesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(capability).orderBy(asc(capability.name));
}

/** Global group categories catalog. */
export function getGroupCategoriesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(group_category).orderBy(asc(group_category.name));
}

/** Declared capabilities per category. */
export function getGroupCategoryCapabilitiesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(group_category_capability);
}

/** Group types (church-specific name), excluding trash. */
export function getGroupTypesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(group_type)
    .where(isNull(group_type.deleted_at))
    .orderBy(asc(group_type.name));
}

/** Grupos visibles (fuera papelera). */
export function getGroupsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(church_group)
    .where(isNull(church_group.deleted_at))
    .orderBy(asc(church_group.name));
}

/** Visible group members (with server-denormalized name). */
export function getGroupMembersQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(group_member).orderBy(asc(group_member.person_name));
}

// ─── F1: music ──────────────────────────────────────────────────────────────

/** Biblioteca de canciones visible (fuera papelera). */
export function getSongsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(song).where(isNull(song.deleted_at)).orderBy(asc(song.title));
}

/** Chords/notes by musician role. */
export function getSongPartsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(song_part).orderBy(asc(song_part.instrument));
}

/** Ensayos visibles (fuera papelera), por fecha. */
export function getRehearsalsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(rehearsal)
    .where(isNull(rehearsal.deleted_at))
    .orderBy(asc(rehearsal.starts_at));
}

/** Setlists visibles (fuera papelera). */
export function getSetlistsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(setlist).where(isNull(setlist.deleted_at)).orderBy(asc(setlist.title));
}

/** Setlist songs (occasion order). */
export function getSetlistSongsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(setlist_song).orderBy(asc(setlist_song.sort_order));
}

// ─── F1: reuniones + actividades + asistencia ───────────────────────────────

/** Visible meetings (excluding trash), sorted by start date. */
export function getMeetingsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(meeting)
    .where(isNull(meeting.deleted_at))
    .orderBy(asc(meeting.starts_at));
}

/** Excepciones de recurrencia visibles. */
export function getMeetingExceptionsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(meeting_exception).orderBy(asc(meeting_exception.occurrence_start));
}

/** Visible meeting guests. */
export function getMeetingInvitesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(meeting_invite).orderBy(asc(meeting_invite.person_name));
}

/** Typed meeting activities (outside trash), in schedule order. */
export function getMeetingActivitiesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(meeting_activity)
    .where(isNull(meeting_activity.deleted_at))
    .orderBy(asc(meeting_activity.sort_order), asc(meeting_activity.created_at));
}

/** Photos per activity (gallery). */
export function getActivityPhotosQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(activity_photo)
    .orderBy(asc(activity_photo.sort_order), asc(activity_photo.created_at));
}

// ─── F2: service (schedule + prayer requests) ───────────────────────────────

/** Bloques de cronograma visibles, en orden de programa. */
export function getScheduleBlocksQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(schedule_block)
    .orderBy(asc(schedule_block.sort_order), asc(schedule_block.created_at));
}

/**
 * Synchronized prayer requests. Local SQLite ONLY contains what
 * streams authorize (mine + public in my scope + private if I have
 * `prayer:manage`); UI defensively re-filters with `visiblePrayerRequests`.
 */
export function getPrayerRequestsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(prayer_request)
    .where(isNull(prayer_request.deleted_at))
    .orderBy(asc(prayer_request.created_at));
}

/** Sesiones de asistencia visibles (fuera papelera). */
export function getAttendanceSessionsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(attendance_session)
    .where(isNull(attendance_session.deleted_at))
    .orderBy(asc(attendance_session.occurrence_start));
}

/** Check-ins de asistencia visibles. */
export function getAttendanceEntriesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(attendance_entry).orderBy(asc(attendance_entry.person_name));
}

// ─── F3: finanzas (tesorero) ─────────────────────────────────────────────────

/**
 * Synchronized finance records (outside trash), most recent
 * first. ONLY reach local SQLite with church `finances:read` —
 * isolation is proven by F0b (leakage) and F3 (integration).
 */
export function getFinanceRecordsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(finance_record)
    .where(isNull(finance_record.deleted_at))
    .orderBy(desc(finance_record.occurred_at), desc(finance_record.created_at));
}

/** Fondos/partidas visibles (fuera papelera). */
export function getFundsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(fund)
    .where(isNull(fund.deleted_at))
    .orderBy(asc(fund.sort_order), asc(fund.name));
}

/** Visible finance categories (outside trash). */
export function getFinanceCategoriesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(finance_category)
    .where(isNull(finance_category.deleted_at))
    .orderBy(asc(finance_category.sort_order), asc(finance_category.name));
}

// ─── F4: devocionales (seguimiento por grupo) ────────────────────────────────

/** Planes de devocionales visibles (fuera papelera). */
export function getDevotionalPlansQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(devotional_plan)
    .where(isNull(devotional_plan.deleted_at))
    .orderBy(desc(devotional_plan.created_at));
}

/** Visible devotional items (outside trash), in schedule order. */
export function getDevotionalItemsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(devotional_item)
    .where(isNull(devotional_item.deleted_at))
    .orderBy(asc(devotional_item.sort_order), asc(devotional_item.created_at));
}

/**
 * Synchronized devotional progress. Local SQLite ONLY contains what
 * streams autorizan: MI progreso (`my_devotional_progress`) y, si tengo
 * `devotional-tracking:manage` of group, whole group progress
 * (`devotional_moderation`). Regular member NEVER has another member's
 * reflection here (isolation tested in discipleship.rs + e2e).
 */
export function getDevotionalProgressQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(devotional_progress).orderBy(asc(devotional_progress.created_at));
}

/** Synchronized moderator follow-ups (mine as author or moderator). */
export function getDevotionalFollowupsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(devotional_followup).orderBy(asc(devotional_followup.created_at));
}

// ─── F4: teatro/danza (obras + elementos de escena) ─────────────────────────

/** Visible works (scripts/choreographies) (outside trash). */
export function getSceneWorksQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(scene_work)
    .where(isNull(scene_work.deleted_at))
    .orderBy(asc(scene_work.title));
}

/** Elementos de escena visibles (fuera papelera). */
export function getSceneElementsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(scene_element)
    .where(isNull(scene_element.deleted_at))
    .orderBy(asc(scene_element.name));
}

/** Gallery of works. */
export function getScenePhotosQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(scene_photo)
    .orderBy(asc(scene_photo.sort_order), asc(scene_photo.created_at));
}

// ─── F5-residual: limpieza (roster de turnos) ────────────────────────────────

/** Visible cleaning shifts (outside trash), upcoming first. */
export function getCleaningShiftsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(cleaning_shift)
    .where(isNull(cleaning_shift.deleted_at))
    .orderBy(asc(cleaning_shift.shift_date), asc(cleaning_shift.created_at));
}

/** Person assignments to shifts. */
export function getCleaningAssignmentsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(cleaning_assignment).orderBy(asc(cleaning_assignment.created_at));
}

// ─── F3: inventario ──────────────────────────────────────────────────────────

/** Visible inventory items (outside trash). */
export function getInventoryItemsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(inventory_item)
    .where(isNull(inventory_item.deleted_at))
    .orderBy(asc(inventory_item.name));
}

/** Inventory ledger movements, most recent first. */
export function getInventoryMovementsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(inventory_movement)
    .orderBy(desc(inventory_movement.occurred_at), desc(inventory_movement.created_at));
}
